'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Modal from '../ui/Modal'
import { showToast } from '../ui/Toast'
import { authApi } from '../../api/endpoints'
import { useCreateDreamTeam, useDreamTeam, useSubmitDreamTeam, useUpdateDreamTeam, useMatchFantasyPlayers } from '../../hooks/useFootball'
import { buildDreamTeamBotLink, DEFAULT_TELEGRAM_BOT_USERNAME } from '../../lib/telegramDeepLink'
import type { FootballMatch } from '../../types'
import { useLocale, useT } from '../../contexts/LocaleContext'
import { LOCALE_META } from '../../i18n'
import {
  PITCH_LAYOUTS,
  POSITION_ICONS,
  assignByPosition,
  buildSlots,
  getPositionLabel,
  toCard,
  type Card_,
} from './pitchLayouts'
import FootballPitchShell from './FootballPitchShell'
import PitchPlayerToken from './PitchPlayerToken'
import {
  downloadBlob,
  dreamTeamImageLabels,
  renderDreamTeamShareMedia,
  type DreamTeamShareMedia,
} from './dreamTeamImage'

// The big match dream team builder: the cards activated for the match are laid
// out as a pitch, the user taps a slot and then picks a card for it.
export default function DreamTeamBuilder({ match }: { match: FootballMatch }) {
  const navigate = useNavigate()
  const t = useT()
  const { locale } = useLocale()
  const [teamName, setTeamName] = useState('')
  const [formation, setFormation] = useState('4-4-2')
  const [lineup, setLineup] = useState<Record<string, Card_>>({})
  const [activeSlotKey, setActiveSlotKey] = useState<string | null>(null)
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null)
  const [cardQuery, setCardQuery] = useState('')
  const [shareOpen, setShareOpen] = useState(false)
  const [shareTeamId, setShareTeamId] = useState<number | null>(null)
  const [mediaBusy, setMediaBusy] = useState(false)
  const [mediaUrl, setMediaUrl] = useState<string | null>(null)
  const [media, setMedia] = useState<DreamTeamShareMedia | null>(null)
  const [botUsername, setBotUsername] = useState(DEFAULT_TELEGRAM_BOT_USERNAME)
  const hydrated = useRef(false)
  const cardsRef = useRef<HTMLDivElement>(null)
  const mediaUrlRef = useRef<string | null>(null)

  useEffect(() => {
    authApi
      .getRandomReferralCode()
      .then((res) => {
        if (res.bot_username) setBotUsername(res.bot_username.replace(/^@/, ''))
      })
      .catch(() => {})
  }, [])

  const { data: poolData } = useMatchFantasyPlayers(match.id)
  const { data: myTeamData, isFetched: teamFetched } = useDreamTeam(match.id)
  const createTeam = useCreateDreamTeam()
  const updateTeam = useUpdateDreamTeam()
  const submitTeam = useSubmitDreamTeam()

  const pool = poolData?.pool
  const allPlayers = pool?.players || []
  const matchGroups = pool?.groups || []

  const savedTeam = myTeamData?.dream_team || null
  const isSubmitted = !!savedTeam?.is_submitted

  const slots = useMemo(() => buildSlots(formation), [formation])
  const pitchRows = useMemo(() => PITCH_LAYOUTS[formation] || PITCH_LAYOUTS['4-4-2'], [formation])
  const slotAt = useMemo(() => {
    const map: Record<string, (typeof slots)[number]> = {}
    slots.forEach((slot) => {
      map[`${slot.row}-${slot.col}`] = slot
    })
    return map
  }, [slots])

  const totalSlots = slots.length
  const selectedCount = slots.filter((s) => lineup[s.key]).length
  const isComplete = selectedCount === totalSlots

  const activeSlot = activeSlotKey ? slots.find((s) => s.key === activeSlotKey) || null : null
  const candidates = useMemo(() => {
    if (!activeSlot) return []
    return allPlayers
      .filter((p) => p.position === activeSlot.position)
      .filter((p) => activeGroupId === null || p.group_ids.includes(activeGroupId))
  }, [allPlayers, activeSlot, activeGroupId])

  const filteredCandidates = useMemo(() => {
    const q = cardQuery.trim()
    if (!q) return candidates
    return candidates.filter((p) => p.name.includes(q))
  }, [candidates, cardQuery])

  useEffect(() => {
    setCardQuery('')
  }, [activeSlotKey])

  const savedLineup = useMemo<Record<string, Card_>>(() => {
    if (!savedTeam?.players) return {}
    const ordered = [...savedTeam.players]
      .sort((a, b) => a.slot_number - b.slot_number)
      .map((tp) => (tp.player ? toCard(tp.player) : null))
      .filter((c): c is Card_ => !!c)
    const map: Record<string, Card_> = {}
    slots.forEach((slot, index) => {
      const card = ordered[index]
      if (card) map[slot.key] = card
    })
    return map
  }, [savedTeam, slots])

  useEffect(() => {
    if (!teamFetched || hydrated.current) return
    // Wait until the formation state matches the saved team, so the slots
    // the lineup is written into are the right shape.
    if (savedTeam && (savedTeam.formation || '4-4-2') !== formation) {
      setTeamName(savedTeam.name)
      setFormation(savedTeam.formation || '4-4-2')
      return
    }
    hydrated.current = true
    if (!savedTeam) {
      setActiveSlotKey('gk-0')
      return
    }
    setTeamName(savedTeam.name)
    setLineup(savedLineup)
    setActiveSlotKey(Object.keys(savedLineup)[0] || 'gk-0')
    setShareTeamId(savedTeam.id)
  }, [teamFetched, savedTeam, savedLineup, formation])

  const focusSlot = (key: string) => {
    setActiveSlotKey(key)
    setTimeout(() => {
      cardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
  }

  const handleFormationChange = (next: string) => {
    setFormation(next)
    const nextSlots = buildSlots(next)
    // Re-flow the picked cards by position so no card ends up outside its position.
    setLineup(assignByPosition(nextSlots, Object.values(lineup)))
  }

  const handlePickCard = (option: Card_) => {
    if (!activeSlotKey || isSubmitted) return
    const currentIndex = slots.findIndex((s) => s.key === activeSlotKey)
    setLineup((prev) => {
      const updated = { ...prev }
      Object.keys(updated).forEach((key) => {
        if (updated[key].id === option.id) delete updated[key]
      })
      updated[activeSlotKey] = option
      return updated
    })

    const isFilled = (key: string) => key === activeSlotKey || !!lineup[key]
    const nextEmpty = [...slots.slice(currentIndex + 1), ...slots.slice(0, currentIndex)].find((s) => !isFilled(s.key))
    if (nextEmpty) setActiveSlotKey(nextEmpty.key)
  }

  const handleRemoveFromSlot = (key: string) => {
    if (isSubmitted) return
    setLineup((prev) => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
    focusSlot(key)
  }

  const buildPayload = () => ({
    name: teamName.trim(),
    formation,
    match_id: match.id,
    players: slots
      .filter((s) => lineup[s.key])
      .map((slot, index) => ({
        player_id: lineup[slot.key].id,
        position: lineup[slot.key].position,
        slot: index + 1,
      })),
  })

  const persistTeam = async (): Promise<number | null> => {
    if (!teamName.trim()) {
      showToast(t('dreamTeam.builder.toastEnterName'), 'error')
      return null
    }
    if (!isComplete) {
      showToast(
        t('dreamTeam.builder.toastPlayersRemaining', {
          remaining: totalSlots - selectedCount,
          selected: selectedCount,
          total: totalSlots,
        }),
        'error',
      )
      return null
    }
    try {
      if (savedTeam && isSubmitted) return savedTeam.id
      if (savedTeam) {
        const updated = await updateTeam.mutateAsync({ teamId: savedTeam.id, ...buildPayload() })
        setShareTeamId(updated.dream_team.id)
        return updated.dream_team.id
      }
      const created = await createTeam.mutateAsync(buildPayload())
      setShareTeamId(created.dream_team.id)
      return created.dream_team.id
    } catch (err: any) {
      showToast(err.response?.data?.message || t('dreamTeam.builder.toastSaveError'), 'error')
      return null
    }
  }

  const handleSave = async () => {
    const existed = !!savedTeam
    const id = await persistTeam()
    if (!id) return
    showToast(existed ? t('dreamTeam.builder.toastUpdated') : t('dreamTeam.builder.toastCreated'), 'success')
  }

  const handleSubmit = async () => {
    if (!savedTeam) return
    try {
      await submitTeam.mutateAsync(savedTeam.id)
      showToast(t('dreamTeam.builder.toastSubmitted'), 'success')
    } catch (err: any) {
      showToast(err.response?.data?.message || t('dreamTeam.builder.toastSubmitError'), 'error')
    }
  }

  const isBusy = createTeam.isPending || updateTeam.isPending

  // Share through the Telegram bot so newcomers hit register+referral,
  // and existing users land on this dream team inside the Mini App.
  const teamLink = shareTeamId
    ? buildDreamTeamBotLink(botUsername, match.id, shareTeamId)
    : `https://t.me/${(botUsername || DEFAULT_TELEGRAM_BOT_USERNAME).replace(/^@/, '')}`

  const matchLabel = t('dreamTeam.matchVs', { home: match.home_team, away: match.away_team })
  const matchDate = (() => {
    try {
      return new Date(match.start_time).toLocaleDateString(LOCALE_META[locale].numberLocale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return match.start_time
    }
  })()

  const revokeMediaUrl = () => {
    if (mediaUrlRef.current) {
      URL.revokeObjectURL(mediaUrlRef.current)
      mediaUrlRef.current = null
    }
    setMediaUrl(null)
    setMedia(null)
  }

  const buildShareMedia = async () => {
    setMediaBusy(true)
    revokeMediaUrl()
    try {
      const safeName = (teamName.trim() || 'dream-team').replace(/[^\w\u0600-\u06FF-]+/g, '-').slice(0, 40)
      const next = await renderDreamTeamShareMedia(
        {
          teamName: teamName.trim() || t('dreamTeam.defaultTeamName'),
          formation,
          lineup,
          matchLabel,
          matchDate,
          link: teamLink,
          labels: dreamTeamImageLabels(t),
        },
        safeName || 'dream-team',
      )
      const url = URL.createObjectURL(next.blob)
      mediaUrlRef.current = url
      setMedia(next)
      setMediaUrl(url)
    } catch {
      showToast(t('dreamTeam.builder.toastAnimationFailed'), 'error')
    } finally {
      setMediaBusy(false)
    }
  }

  useEffect(() => {
    if (shareOpen) {
      void buildShareMedia()
      return
    }
    revokeMediaUrl()
    // regenerate when the sheet opens or the bot link becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareOpen, botUsername, shareTeamId])

  // Never share the roster as text — pitch video/GIF only for Stories.
  const shareMediaNative = async (): Promise<boolean> => {
    if (!media) return false
    const file = new File([media.blob], media.filename, { type: media.blob.type || (media.kind === 'video' ? 'video/mp4' : 'image/gif') })
    const fileOnly: ShareData = { files: [file] }
    const withLink: ShareData = { files: [file], url: teamLink }
    const tryPayload = async (payload: ShareData) => {
      if (typeof navigator.share !== 'function') return false
      if (typeof navigator.canShare === 'function' && !navigator.canShare(payload)) return false
      try {
        await navigator.share(payload)
        return true
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
          return true
        }
        return false
      }
    }
    if (await tryPayload(withLink)) return true
    return tryPayload(fileOnly)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(teamLink)
      showToast(t('dreamTeam.builder.toastLinkCopied'), 'success')
    } catch {
      showToast(teamLink, 'info')
    }
  }

  const handleDownloadMedia = async () => {
    if (!media) {
      showToast(t('dreamTeam.builder.toastFileNotReady'), 'info')
      return
    }
    try {
      await navigator.clipboard.writeText(teamLink)
    } catch {
      // clipboard is best-effort
    }
    downloadBlob(media.blob, media.filename)
    showToast(
      media.kind === 'video'
        ? t('dreamTeam.builder.toastVideoSaved')
        : t('dreamTeam.builder.toastGifSaved'),
      'success',
    )
  }

  /** Story share: video preferred (IG freezes GIFs). Never open t.me/share with roster text. */
  const shareForStory = async () => {
    if (!media) {
      showToast(t('dreamTeam.builder.toastWaitForFile'), 'info')
      return
    }
    const shared = await shareMediaNative()
    if (shared) return
    await handleDownloadMedia()
    showToast(
      media.kind === 'video'
        ? t('dreamTeam.builder.toastStoryStepsVideo')
        : t('dreamTeam.builder.toastStoryStepsFile'),
      'info',
    )
  }

  const handleShare = () => {
    if (!savedTeam) return
    setShareTeamId(savedTeam.id)
    setShareOpen(true)
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('dreamTeam.builder.labelTeamName')}</label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          maxLength={40}
          disabled={isSubmitted}
          placeholder={t('dreamTeam.builder.placeholderTeamName')}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:outline-none disabled:opacity-60"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('dreamTeam.builder.labelFormation')}</label>
        <select
          value={formation}
          onChange={(e) => handleFormationChange(e.target.value)}
          disabled={isSubmitted}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white disabled:opacity-60"
        >
          {Object.keys(PITCH_LAYOUTS).map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {matchGroups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-400 w-full">{t('dreamTeam.builder.activeCardGroups')}</span>
          {matchGroups.map((group) => (
            <span key={group.id} className="text-xs bg-gray-800 rounded-full px-3 py-1 text-gray-300">
              {group.title}
            </span>
          ))}
        </div>
      )}

      {allPlayers.length === 0 && (
        <p className="text-xs text-yellow-400">
          {t('dreamTeam.builder.noActiveCards')}
        </p>
      )}

      <FootballPitchShell
        boardText={
          teamName.trim()
            ? `${teamName.trim()} · ${match.home_team} vs ${match.away_team}`
            : `${match.home_team} vs ${match.away_team}`
        }
      >
        {pitchRows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-5 gap-1 items-center">
            {[0, 1, 2, 3, 4].map((col) => {
              const slot = slotAt[`${rowIndex}-${col}`]
              if (!slot) return <div key={col} />
              const player = lineup[slot.key]
              return (
                <PitchPlayerToken
                  key={col}
                  player={player}
                  position={slot.position}
                  isActive={activeSlotKey === slot.key && !isSubmitted}
                  emptyHint={isSubmitted ? undefined : t('dreamTeam.builder.pickEmpty')}
                  floatKey={rowIndex * 5 + col}
                  locked={isSubmitted}
                  onClick={() => focusSlot(slot.key)}
                  onRemove={isSubmitted ? undefined : () => handleRemoveFromSlot(slot.key)}
                />
              )
            })}
          </div>
        ))}
      </FootballPitchShell>

      {!isSubmitted && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">
            {t('dreamTeam.builder.playersSelected', { selected: selectedCount, total: totalSlots })}
          </span>
          {savedTeam && <span className="text-xs text-yellow-400">{t('dreamTeam.status.notSubmitted')}</span>}
        </div>
      )}

      {/* Card groups of this match */}
      {!isSubmitted && matchGroups.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveGroupId(null)}
            className={`shrink-0 text-xs rounded-full px-3 py-1.5 border ${
              activeGroupId === null ? 'border-primary bg-primary/15 text-white' : 'border-gray-700 text-gray-400'
            }`}
          >
            {t('dreamTeam.builder.filterAll', { count: allPlayers.length })}
          </button>
          {matchGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => setActiveGroupId(group.id)}
              className={`shrink-0 text-xs rounded-full px-3 py-1.5 border ${
                activeGroupId === group.id ? 'border-primary bg-primary/15 text-white' : 'border-gray-700 text-gray-400'
              }`}
            >
              {group.title} ({allPlayers.filter((p) => p.group_ids.includes(group.id)).length})
            </button>
          ))}
        </div>
      )}

      {/* Cards for the selected slot */}
      {!isSubmitted && (
        <Card className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-sm">
              {activeSlot
                ? t('dreamTeam.builder.pickPositionTitle', {
                    icon: POSITION_ICONS[activeSlot.position],
                    position: getPositionLabel(activeSlot.position, t),
                  })
                : t('dreamTeam.builder.pickSlotHint')}
            </h4>
            <span className="text-xs text-gray-400">{t('dreamTeam.builder.cardCount', { count: candidates.length })}</span>
          </div>

          {!activeSlot ? (
            <p className="text-center text-sm text-gray-400 py-6">{t('dreamTeam.builder.tapEmptySlot')}</p>
          ) : candidates.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-6">
              {t('dreamTeam.builder.noCardsForPosition')}
            </p>
          ) : (
            <>
              <input
                type="text"
                value={cardQuery}
                onChange={(e) => setCardQuery(e.target.value)}
                placeholder={t('dreamTeam.builder.searchPlayerPlaceholder')}
                className="w-full px-3 py-2 mb-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:border-primary focus:outline-none"
              />
              <div ref={cardsRef} className="space-y-2 max-h-80 overflow-y-auto pb-2 pl-1">
                {filteredCandidates.map((option) => {
                  const inThisSlot = activeSlotKey ? lineup[activeSlotKey]?.id === option.id : false
                  const elsewhere = slots.some((s) => s.key !== activeSlotKey && lineup[s.key]?.id === option.id)
                  return (
                    <button
                      key={option.id}
                      onClick={() => handlePickCard(option)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-right transition active:scale-[0.99] ${
                        inThisSlot
                          ? 'border-primary ring-2 ring-primary/50 bg-primary/10'
                          : 'border-gray-700 bg-gray-800/50'
                      }`}
                    >
                      <span className="text-lg leading-none">{POSITION_ICONS[option.position]}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm truncate">{option.name}</span>
                        <span className="block text-[10px] text-gray-400 truncate">
                          {option.group_titles[0] || option.team}
                        </span>
                      </span>
                      {inThisSlot ? (
                        <span className="shrink-0 text-[10px] bg-primary rounded-full px-2 py-0.5">
                          {t('dreamTeam.builder.badgeSelected')}
                        </span>
                      ) : elsewhere ? (
                        <span className="shrink-0 text-[10px] bg-yellow-500/20 text-yellow-300 rounded-full px-2 py-0.5">
                          {t('dreamTeam.builder.badgeInTeam')}
                        </span>
                      ) : (
                        <span className="shrink-0 text-[10px] text-gray-500">{t('dreamTeam.builder.badgeAdd')}</span>
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </Card>
      )}

      {isSubmitted && (
        <Card className="p-3">
          <h4 className="font-bold mb-2 text-sm text-green-400">{t('dreamTeam.status.teamSubmittedHeading')}</h4>
          <p className="text-xs text-gray-400">
            {t('dreamTeam.builder.submittedBlurb')}
          </p>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              readOnly
              value={teamLink}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300"
              dir="ltr"
            />
            <Button variant="secondary" size="sm" onClick={handleCopyLink} className="shrink-0">
              {t('dreamTeam.builder.copyLink')}
            </Button>
          </div>
        </Card>
      )}

      <div className="flex gap-2">
        {savedTeam && (
          <Button variant="outline" className="flex-1" onClick={handleShare} disabled={isBusy}>
            {t('dreamTeam.builder.shareTeam')}
          </Button>
        )}
        <Button
          variant="secondary"
          className={savedTeam ? 'flex-1' : 'w-full'}
          onClick={() => navigate('/football/dream-teams/explore')}
        >
          {t('dreamTeam.explore.exploreTeams')}
        </Button>
      </div>

      {!isSubmitted && (
        <div className="flex gap-2 pb-6">
          {savedTeam && (
            <Button variant="secondary" className="flex-1" onClick={handleSubmit} disabled={submitTeam.isPending || isBusy}>
              {t('dreamTeam.builder.submitTeam')}
            </Button>
          )}
          <Button className="flex-1" onClick={handleSave} disabled={isBusy}>
            {isBusy
              ? t('dreamTeam.builder.saving')
              : savedTeam
                ? t('dreamTeam.builder.updateTeam')
                : t('dreamTeam.builder.buildTeam', { selected: selectedCount, total: totalSlots })}
          </Button>
        </div>
      )}

      <Modal open={shareOpen} onClose={() => setShareOpen(false)} title={t('dreamTeam.builder.shareModal.title')}>
        <div className="space-y-3">
          <div className="rounded-xl overflow-hidden bg-black/40 border border-gray-700 flex items-center justify-center min-h-[220px]">
            {mediaBusy && !mediaUrl ? (
              <p className="text-sm text-gray-400 py-16">{t('dreamTeam.builder.shareModal.buildingComposition')}</p>
            ) : mediaUrl && media?.kind === 'video' ? (
              <video
                src={mediaUrl}
                className="w-full max-h-[360px] object-contain"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : mediaUrl ? (
              <img
                src={mediaUrl}
                alt={t('dreamTeam.view.pitchAlt')}
                className="w-full max-h-[360px] object-contain"
              />
            ) : (
              <p className="text-sm text-gray-500 py-16">{t('dreamTeam.builder.shareModal.fileNotReady')}</p>
            )}
          </div>

          <p className="text-xs text-amber-200/90 leading-relaxed bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">
            {t('dreamTeam.builder.shareModal.instagramTip')}{' '}
            {t('dreamTeam.builder.shareModal.instagramSteps')}
          </p>

          <Button
            variant="primary"
            className="w-full"
            onClick={shareForStory}
            disabled={mediaBusy || !media}
          >
            {mediaBusy
              ? t('dreamTeam.builder.shareModal.building')
              : media?.kind === 'video'
                ? t('dreamTeam.builder.shareModal.shareVideo')
                : t('dreamTeam.builder.shareModal.shareGif')}
          </Button>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={handleDownloadMedia} disabled={mediaBusy || !media}>
              {media?.kind === 'video'
                ? t('dreamTeam.builder.shareModal.downloadVideo')
                : t('dreamTeam.builder.shareModal.downloadGif')}
            </Button>
            <Button variant="outline" onClick={handleCopyLink}>
              {t('dreamTeam.builder.shareModal.copyBotLink')}
            </Button>
          </div>

          <p className="text-[11px] text-gray-500 break-all" dir="ltr">{teamLink}</p>
        </div>
      </Modal>
    </div>
  )
}